//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { useEffect, useMemo, useState } from "react";
import { Header, Icon, Label, Message, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { MasterPackPlan as ApiMasterPackPlan, Node as ApiNode, NodePod as ApiNodePod, PackPlanStatus } from "../../api/data/v1/idata";
import { compareAvailabilityZone, normalizePackPlanStatus } from "./packPlan";

type RelevantNodeGroup = {
  node: ApiNode;
  pods: ApiNodePod[];
};

type DeploymentRole = "coordinator" | "primary" | "gateway" | "loadbalancer";

const deploymentRoleOrder: DeploymentRole[] = ["coordinator", "primary", "gateway", "loadbalancer"];

const deploymentRoleLabel: Record<DeploymentRole, string> = {
  coordinator: "coordinator",
  primary: "primary",
  gateway: "gateway",
  loadbalancer: "loadbalancer",
};

const classifyDeploymentRole = (pod: ApiNodePod): DeploymentRole | undefined => {
  const candidates = [
    pod.labels?.["deployment.arangodb.com/role"],
    pod.labels?.role,
    pod.labels?.["server-role"],
    pod.labels?.["network-role"],
    pod.labels?.app,
  ];
  for (const raw of candidates) {
    const value = (raw || "").toLowerCase();
    if (!value) {
      continue;
    }
    if (value === "dbserver" || value === "primary") {
      return "primary";
    }
    if (value === "coordinator") {
      return "coordinator";
    }
    if (value === "gateway" || value === "gateways") {
      return "gateway";
    }
    if (value === "load-balancer" || value === "loadbalancer") {
      return "loadbalancer";
    }
  }
  return undefined;
};

const deploymentRolesOnNode = (pods: ApiNodePod[], deploymentId: string): Set<DeploymentRole> => {
  const roles = new Set<DeploymentRole>();
  for (const pod of pods) {
    if (!(pod.namespace || "").includes(deploymentId)) {
      continue;
    }
    const role = classifyDeploymentRole(pod);
    if (role) {
      roles.add(role);
    }
  }
  return roles;
};

// Packed comes from the pack plan. Leftover dedicated load-balancer pods do not
// keep a shared-load-balancer deployment from being packed.
const packedTargetNodes = (plan: ApiMasterPackPlan | undefined): Set<string> => {
  const nodes = new Set<string>();
  const byNode: Record<string, boolean> = {};
  for (const pack of plan?.packs || []) {
    const name = pack.target_node_name || "";
    if (!name) {
      continue;
    }
    const packed = normalizePackPlanStatus(pack.status) === PackPlanStatus.PACK_PLAN_STATUS_ALREADY_PACKED;
    byNode[name] = byNode[name] === undefined ? packed : byNode[name] && packed;
  }
  Object.keys(byNode).forEach((name) => {
    if (byNode[name]) {
      nodes.add(name);
    }
  });
  return nodes;
};

const mono = { fontFamily: "monospace", fontSize: 12 } as const;

type RelevantNodesPanelProps = {
  deploymentId?: string;
  dataclusterId?: string;
  plan?: ApiMasterPackPlan;
  /** Bump to reload nodes together with Refresh plan. */
  refreshToken?: number;
};

/** Prototype: all pods on nodes that host at least one deployment pod (namespace contains deployment id). */
export const RelevantNodesPanel = ({ deploymentId, dataclusterId, plan, refreshToken = 0 }: RelevantNodesPanelProps) => {
  const [nodes, setNodes] = useState<ApiNode[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setExpanded({});
  }, [dataclusterId, deploymentId]);

  useEffect(() => {
    if (!dataclusterId) {
      setNodes(undefined);
      setError(undefined);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(undefined);
      try {
        const dc = await apiClients.idashboardClient.GetDataCluster({ id: dataclusterId });
        if (!cancelled) {
          setNodes(dc.status?.nodes?.items || []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || String(e));
          setNodes(undefined);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dataclusterId, refreshToken]);

  const groups = useMemo(() => {
    if (!deploymentId || !nodes) {
      return [] as RelevantNodeGroup[];
    }
    const out: RelevantNodeGroup[] = [];
    for (const node of nodes) {
      const pods = node.pods || [];
      const hasDeploymentPod = pods.some((p) => (p.namespace || "").includes(deploymentId));
      if (!hasDeploymentPod) {
        continue;
      }
      const sorted = [...pods].sort((a, b) => {
        const aDep = (a.namespace || "").includes(deploymentId) ? 0 : 1;
        const bDep = (b.namespace || "").includes(deploymentId) ? 0 : 1;
        if (aDep !== bDep) {
          return aDep - bDep;
        }
        const ns = (a.namespace || "").localeCompare(b.namespace || "");
        if (ns !== 0) {
          return ns;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
      out.push({ node, pods: sorted });
    }
    out.sort((a, b) => {
      const zone = compareAvailabilityZone(a.node.info?.zone, b.node.info?.zone);
      if (zone !== 0) {
        return zone;
      }
      return (a.node.name || "").localeCompare(b.node.name || "");
    });
    return out;
  }, [deploymentId, nodes]);

  const packedNodes = useMemo(() => packedTargetNodes(plan), [plan]);

  if (!deploymentId) {
    return null;
  }

  const toggle = (name: string) => {
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Header as="h4" style={{ marginBottom: 8 }}>
        Relevant nodes
      </Header>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        Nodes that host a pod whose namespace contains <code>{deploymentId}</code>
        {dataclusterId ? (
          <>
            {" "}
            (datacluster <code>{dataclusterId}</code>)
          </>
        ) : (
          " — no datacluster assignment"
        )}
        ; click a node to show its pods.
      </p>
      {!dataclusterId && <Message warning content="No datacluster assignment; cannot load nodes." />}
      {error && <Message negative content={error} />}
      {loading && <Message info content="Loading nodes…" />}
      {!loading && !error && dataclusterId && groups.length === 0 && (
        <Message content="No nodes with a deployment pod in namespace (or node pod inventory not populated yet)." />
      )}
      {groups.map((g) => {
        const name = g.node.name || "";
        const zone = g.node.info?.zone;
        const instanceType = g.node.info?.instanceType;
        const deploymentPodCount = g.pods.filter((p) => (p.namespace || "").includes(deploymentId)).length;
        const roles = deploymentRolesOnNode(g.pods, deploymentId);
        const hasPrimary = roles.has("primary");
        const packed = packedNodes.has(name);
        const misplaced = !packed && !hasPrimary && roles.size > 0;
        const isOpen = !!expanded[name];
        const border = packed ? "1px solid #bbf7d0" : misplaced ? "1px solid #fdba74" : hasPrimary ? "1px solid #bfdbfe" : "1px solid #e5e7eb";
        const headerBackground = packed
          ? isOpen
            ? "#dcfce7"
            : "#f0fdf4"
          : misplaced
          ? isOpen
            ? "#ffedd5"
            : "#fff7ed"
          : hasPrimary
          ? isOpen
            ? "#dbeafe"
            : "#eff6ff"
          : isOpen
          ? "#f3f4f6"
          : "#f9fafb";
        return (
          <div
            key={name}
            style={{
              marginBottom: 10,
              border,
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggle(name)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(name);
                }
              }}
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                background: headerBackground,
                borderBottom: isOpen ? "1px solid #e5e7eb" : undefined,
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Icon name={isOpen ? "chevron down" : "chevron right"} style={{ margin: 0, color: "#6b7280" }} />
              <span style={{ ...mono, fontWeight: 600, fontSize: 13 }}>{name || "—"}</span>
              {instanceType && (
                <Label size="tiny" basic>
                  {instanceType}
                </Label>
              )}
              {zone && (
                <Label size="tiny" basic>
                  {zone}
                </Label>
              )}
              {packed && (
                <Label size="tiny" basic color="green">
                  packed
                </Label>
              )}
              {deploymentRoleOrder
                .filter((role) => roles.has(role))
                .map((role) => (
                  <Label key={role} size="tiny" basic>
                    {deploymentRoleLabel[role]}
                  </Label>
                ))}
              {g.node.scheduling_v2 && (
                <Label size="tiny" basic>
                  sched v2
                </Label>
              )}
              <Label size="tiny" basic>
                {g.node.status || "unknown"}
              </Label>
              <span style={{ color: "#6b7280", fontSize: 12, marginLeft: "auto" }}>
                {deploymentPodCount} {deploymentPodCount === 1 ? "member" : "members"} / {g.pods.length} total pods
              </span>
            </div>
            {isOpen && (
              <Table compact celled size="small" style={{ margin: 0, borderRadius: 0, border: "none", background: "#fff" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={1} />
                    <Table.HeaderCell>Namespace</Table.HeaderCell>
                    <Table.HeaderCell>Pod</Table.HeaderCell>
                    <Table.HeaderCell>Phase</Table.HeaderCell>
                    <Table.HeaderCell>Ready</Table.HeaderCell>
                    <Table.HeaderCell>CPU</Table.HeaderCell>
                    <Table.HeaderCell>Memory</Table.HeaderCell>
                    <Table.HeaderCell>Controller</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {g.pods.map((p) => {
                    const isDeployment = (p.namespace || "").includes(deploymentId);
                    return (
                      <Table.Row key={`${p.namespace}/${p.name}`} style={{ background: isDeployment ? "#f0f9ff" : "#fff" }}>
                        <Table.Cell textAlign="center" style={{ color: "#9ca3af" }}>
                          {isDeployment ? "●" : "○"}
                        </Table.Cell>
                        <Table.Cell>
                          <span style={mono}>{p.namespace || "—"}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span style={{ ...mono, fontWeight: isDeployment ? 600 : 400 }}>{p.name || "—"}</span>
                        </Table.Cell>
                        <Table.Cell>{p.phase || "—"}</Table.Cell>
                        <Table.Cell>{p.ready ? "Yes" : "No"}</Table.Cell>
                        <Table.Cell>
                          <span style={mono}>{p.cpu_request || "—"}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span style={mono}>{p.memory_request || "—"}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span style={mono}>{p.controller_kind || p.controller_name ? `${p.controller_kind || "?"}/${p.controller_name || "?"}` : "—"}</span>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            )}
          </div>
        );
      })}
    </div>
  );
};
